import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Top-level Agent V2 chat container.
//
// Renders header (title + roster + model badge + Stop button), scrolling
// message list, jump-to-latest button, and the multi-prompter composer.
// Theme/density/layout flow through `data-` attributes so the OKLCH
// tokens in `theme.css` (rescoped under `[data-agent-v2]`) resolve
// correctly.
//
// This is a controlled component — the consumer owns the message list,
// so wiring to live channel data (KTK-211 plumbing) and to the runtime's
// outgoing-channel-message RPC happens at the higher AgentV2ChatView
// layer.
import { useEffect, useRef, useState } from "react";
import { renderBlock } from "./blocks.js";
import { Composer } from "./Composer.js";
import { Icon } from "./icons.js";
import { MessageGroup } from "./MessageGroup.js";
import { Msg, TypingMsg } from "./Msg.js";
export function Chat({ title, roster, participants, postingAs, onSelectAs, messages, showTyping = null, onSend, busy = false, onStop, onReact, model, theme = "dark", density = "compact", layout = "bubble", }) {
    const scrollRef = useRef(null);
    const [showJump, setShowJump] = useState(false);
    // Initial-load history fetches asynchronously (parent seeds with
    // `[]` and resolves getChannelMessages after mount), so a mount-only
    // scroll-to-bottom effect ran before any messages arrived and never
    // fired again. We instead pin once, on the first non-empty render.
    const initialScrollDone = useRef(false);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        const onScroll = () => {
            const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
            setShowJump(dist > 80);
        };
        el.addEventListener("scroll", onScroll);
        onScroll();
        return () => el.removeEventListener("scroll", onScroll);
    }, []);
    // Pin to bottom whenever messages change. Three force-scroll cases
    // override the otherwise-conservative "only stick when already near
    // bottom" rule:
    //
    // 1. **First non-empty render.** Channel history loads async, so
    //    we pin once the first batch lands.
    // 2. **User just sent.** Optimistic insert from onSend; they hit
    //    Send and expect to see their text at the bottom.
    // 3. **Live activity bubble at end.** KTK-236 — while the agent is
    //    mid-turn, blocks accumulate inside an `id: live-<turnId>`
    //    placeholder. The bubble grows continuously (text deltas, tool
    //    blocks, reasoning) and the scroll position needs to follow it
    //    so the user actually sees the response come in.
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        const last = messages[messages.length - 1];
        const userJustSent = last != null && last.author === postingAs;
        const liveBubbleAtEnd = last != null && last.id.startsWith("live-");
        const firstNonEmpty = !initialScrollDone.current && messages.length > 0;
        if (userJustSent || liveBubbleAtEnd || firstNonEmpty) {
            el.scrollTop = el.scrollHeight;
            if (firstNonEmpty)
                initialScrollDone.current = true;
            return;
        }
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (dist < 80) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages.length, showTyping, postingAs, messages]);
    const jumpToBottom = () => {
        const el = scrollRef.current;
        if (el)
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    };
    // Title parsing from the mockup — split on " · " so the suffix gets
    // a softer italic tone.
    const [titleHead, titleTail] = splitTitle(title);
    return (_jsxs("div", { className: "chat", "data-agent-v2": "", "data-theme": theme, "data-density": density, "data-layout": layout, children: [_jsxs("div", { className: "chat__head", children: [_jsx("div", { className: "chat__head-l", children: _jsxs("span", { className: "chat__title", children: [titleHead, titleTail && _jsx("em", { children: titleTail })] }) }), _jsxs("div", { className: "chat__roster", children: [Object.values(roster).map((p) => (_jsxs("span", { className: "chat__roster-name", title: p.name, children: [_jsx("span", { className: "dot", style: { background: `var(${p.colorVar})` } }), p.name] }, p.id))), model && _jsx("span", { className: "chat__model", children: model })] })] }), _jsxs("div", { className: "chat__scroll", ref: scrollRef, children: [_jsxs("div", { className: "chat__day", children: ["Today, ", todayLabel()] }), messages.map((m) => {
                        const author = roster[m.author];
                        if (!author)
                            return null;
                        return (_jsx(MessageGroup, { author: author, time: m.time, currentUser: postingAs, children: _jsxs(Msg, { id: m.id, reactions: m.reactions, threadCount: m.threadCount, edited: m.edited, onReact: onReact ? (e) => onReact(m.id, e) : undefined, children: [m.blocks.map((b, i) => renderBlock(b, i)), (m.tokens || m.model) && roster[m.author]?.role === "ai" && (_jsxs("div", { className: "tokmeta", children: [m.model && (_jsxs("span", { className: "tokmeta__model", title: `Generated by ${m.model}`, children: [_jsx(Icon.spark, {}), _jsx("span", { children: m.model })] })), m.model && m.tokens && _jsx("span", { className: "tokmeta__sep" }), m.tokens && (_jsxs(_Fragment, { children: [_jsxs("span", { children: [m.tokens.in.toLocaleString(), " in"] }), _jsx("span", { className: "tokmeta__sep" }), _jsxs("span", { children: [m.tokens.out.toLocaleString(), " out"] }), _jsx("span", { className: "tokmeta__sep" }), _jsx("span", { children: m.tokens.cost })] }))] }))] }) }, m.id));
                    }), showTyping && roster[showTyping] && (_jsx(MessageGroup, { author: roster[showTyping], time: "now", currentUser: postingAs, children: _jsx(TypingMsg, {}) })), showJump && (_jsxs("button", { type: "button", className: "jump", onClick: jumpToBottom, children: [_jsx(Icon.arrowDown, {}), " jump to latest"] }))] }), _jsx(Composer, { roster: roster, participants: participants, as: postingAs, onSelectAs: onSelectAs, onSend: onSend, model: model, busy: busy, onStop: onStop })] }));
}
function splitTitle(title) {
    const idx = title.indexOf("·");
    if (idx === -1)
        return [title, ""];
    return [title.slice(0, idx).trim() + " ", "· " + title.slice(idx + 1).trim()];
}
function todayLabel() {
    return new Date().toLocaleDateString(undefined, { month: "long", day: "numeric" });
}
//# sourceMappingURL=Chat.js.map